import { Box } from '@mui/material'
import {
  GridFooterContainer,
  GridPagination,
  GridRowClassNameParams,
  useGridApiRef,
  type GridFilterModel,
  type GridPaginationModel,
  type GridSortModel,
} from '@mui/x-data-grid-premium'
import DataGrid from 'components/DataGrid/DataGrid'
import COLORS from 'lib/colors'
import { useOrbit } from 'providers/OrbitProvider'
import { failOnHttpError } from 'lib/utils'
import { useNotifications } from 'lib/NotificationsProvider'
import LeadDetailsDrawer from '../components/LeadDetailsDrawer'
import { buildCustomFieldTypes, buildLeadsRequestBody } from '../leadTableQuery'
import useLeadsRequest from './useLeadsRequest'
import AiChatContainer from '../ai/AiChatContainer'
import { LeadRow, LeadTableProps } from './leadTableTypes'
import { mapLeadToRow } from './leadTableMappers'
import { useCallback, useMemo, useState } from 'react'
import { buildLeadTableColumns } from './buildLeadTableColumns'
import { buildLeadTableColumnsWithFilters } from '../filters/buildLeadTableColumnsWithFilters'
import { applyColumnOrder, savePageSize } from '../leadTablePreferences'
import LeadTableToolbar from './LeadTableToolbar'
import LeadTableFilterOperatorOption from '../filters/LeadTableFilterOperatorOption'
import LeadTableColumnsManagement from './LeadTableColumnManagement'


const PAGE_SIZE_OPTIONS = [25, 50, 100]

const DATA_GRID_INITIAL_STATE = {
  pinnedColumns: { right: ['rowMenu'] },
}

const DATA_GRID_SX = {
  border: `1px solid ${COLORS.mediumGray}`,
  borderBottom: 'none',
  boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
  borderRadius: '10px',
  ['.MuiDataGrid-columnHeader, .MuiDataGrid-cell']: {
    pl: 2,
  },
  '& .even': {
    backgroundColor: 'white',
  },
  '& .odd': {
    backgroundColor: COLORS.backgroundGray,
  },
  '& .even .MuiDataGrid-cell.menu-col': {
    backgroundColor: 'white',
  },
  '& .odd .MuiDataGrid-cell.menu-col': {
    backgroundColor: COLORS.backgroundGray,
  },
  '& .MuiDataGrid-columnHeader.menu-col, & .MuiDataGrid-cell.menu-col': {
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  '& .MuiDataGrid-menuIconButton, & .MuiDataGrid-sortIcon, & .MuiDataGrid-filterIcon': {
    color: COLORS.slateGray,
  },
}

const DATA_GRID_SLOTS = {
  footer: LeadTableFooter,
  toolbar: LeadTableToolbar,
  baseSelectOption: LeadTableFilterOperatorOption,
  columnsManagement: LeadTableColumnsManagement,
}

function getLeadRowClassName(params: GridRowClassNameParams<LeadRow>) {
  return params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
}

function removePanelHiddenFilters(filterModel: GridFilterModel): GridFilterModel {
  return {
    ...filterModel,
    items: filterModel.items.filter((item) => item.operator !== 'between'),
  }
}

export type LeadApiRecord = {
  id: string
  type: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes: Record<string, any>
  relationships?: Record<string, unknown>
}

type LeadsApiResponse = {
  data: LeadApiRecord[]
  meta?: {
    total?: number
  }
}

type LeadTableFooterProps = {
  permitAiAnalysis: boolean
  totalCount: number
  loading: boolean
  requestBodyJson: string
}

function LeadTableFooter({ permitAiAnalysis, totalCount, loading, requestBodyJson }: LeadTableFooterProps) {

  return (
    <GridFooterContainer>
      <Box sx={{ p: 1 }}>
        {permitAiAnalysis &&
          <AiChatContainer
            totalCount={totalCount}
            loading={loading}
            requestBodyJson={requestBodyJson}
          />
        }
      </Box>
      <Box sx={{ ml: 'auto' }}>
        <GridPagination />
      </Box>
    </GridFooterContainer>
  )
}

export function LeadsDataGrid({
  timezone,
  refreshKey = 0,
  refreshTable,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  columnOrder,
  onColumnOrderChange,
  onResetColumns,
  clientIdToName,
  clientCampaignIdToName,
  vendorIdToName,
  degreeProgramIdToName,
  degreeProgramsByClient,
  campaignsByClient,
  statuses,
  dispositions,
  clientCampaignCustomAttributes,
  leadCustomFields,
  permitLandingPages,
  canDeleteLead,
  permitAiAnalysis,
  userHasMultipleClients,
  userHasMultipleVendors,
  onResetFilters,
  onExport,
  onImport,
  exportStatus,
  permitUpload,
  tableSettings,
  updateTableSettings,
}: LeadTableProps) {
  const [showLeadDetails, setShowLeadDetails] = useState<LeadRow | null>()

  const { remote } = useOrbit()
  const { addNotification } = useNotifications()
  const apiRef = useGridApiRef()

  const customFieldTypes = useMemo(
    () => buildCustomFieldTypes(leadCustomFields ?? []),
    [leadCustomFields]
  )

  const requestBody = useMemo(() => {
    return buildLeadsRequestBody({
      paginationModel,
      sortModel,
      filterModel,
      timezone,
      customFieldTypes,
    })
  }, [paginationModel, sortModel, filterModel, timezone, customFieldTypes])

  const requestBodyJson = useMemo(() => {
    return JSON.stringify(requestBody)
  }, [requestBody])

  const { data: response, error, loading } = useLeadsRequest<LeadsApiResponse>(
    '/api/v1/leads',
    requestBody,
    refreshKey
  )

  const handleLeadDetails = useCallback((row: LeadRow) => {
    setShowLeadDetails(row)
  }, [])

  const handleHideDetails = useCallback(() => {
    setShowLeadDetails(null)
  }, [])

  const handleDeleteLead = useCallback((row: LeadRow) => {
    const leadName = [row.firstName, row.lastName].filter(Boolean).join(' ').trim()
    const isMaskedLeadName = leadName !== '' && /^[*\s]+$/.test(leadName)
    const leadLabel = !leadName || isMaskedLeadName ? `with ID: ${row.id}` : `record for ${leadName}`

    if (!confirm(`Are you sure you want to delete the lead ${leadLabel}?`)) {
      return
    }

    fetch(remote.requestProcessor.urlBuilder.resourceURL('lead', row.id), {
      method: 'DELETE',
    })
      .then(failOnHttpError)
      .then(() => {
        addNotification({ variant: 'notice', message: 'Deleted lead.' })
        refreshTable()
      })
      .catch(() => {
        addNotification({ variant: 'alert', message: 'Failed to delete lead.' })
      })
  }, [addNotification, refreshTable, remote.requestProcessor.urlBuilder])

  const rows = useMemo(() => {
    return response?.data?.map(mapLeadToRow) ?? []
  }, [response])

  const columns = useMemo(() => {
    const unorderedColumns = buildLeadTableColumns({
      timezone,
      clientIdToName,
      clientCampaignIdToName,
      vendorIdToName,
      degreeProgramIdToName,
      degreeProgramsByClient,
      campaignsByClient,
      statuses,
      dispositions,
      permitLandingPages,
      userHasMultipleClients,
      userHasMultipleVendors,
      canDeleteLead,
      onLeadDetails: handleLeadDetails,
      onDeleteLead: handleDeleteLead,
      clientCampaignCustomAttributes,
      leadCustomFields: leadCustomFields ?? [],
    })

    return applyColumnOrder(unorderedColumns, columnOrder, columnVisibilityModel)
  }, [timezone, clientIdToName, clientCampaignIdToName, vendorIdToName, degreeProgramIdToName, degreeProgramsByClient, campaignsByClient, statuses, dispositions, permitLandingPages, userHasMultipleClients, userHasMultipleVendors, canDeleteLead, handleLeadDetails, handleDeleteLead, clientCampaignCustomAttributes, leadCustomFields, columnOrder, columnVisibilityModel])

  const handlePaginationModelChange = useCallback((nextModel: GridPaginationModel) => {
    if (nextModel.pageSize !== paginationModel.pageSize) {
      savePageSize(nextModel.pageSize)
    }

    onPaginationModelChange(nextModel)
  }, [onPaginationModelChange, paginationModel.pageSize])

  const handleSortModelChange = useCallback((nextModel: GridSortModel) => {
    onSortModelChange(nextModel)
  }, [onSortModelChange])

  const handleFilterModelChange = useCallback((nextModel: GridFilterModel) => {
    onFilterModelChange(nextModel)
  }, [onFilterModelChange])

  // this mui-specific handling of the filter model strips out the 'between' filters in use
  // for date fields so they won't show up in the filter panel
  const handleMuiFilterModelChange = useCallback((nextModel: GridFilterModel) => {
    onFilterModelChange({
      ...nextModel,
      items: [
        ...filterModel.items.filter((item) => item.operator === 'between'),
        ...nextModel.items,
      ],
    })
  }, [
    filterModel,
    onFilterModelChange,
  ])

  const muiFilterModel = useMemo(
    () => removePanelHiddenFilters(filterModel),
    [filterModel],
  )

  const handleColumnOrderChange = useCallback(() => {
    const api = apiRef.current
    if (!api) return

    onColumnOrderChange(api.getAllColumns().map((column) => column.field))
  }, [apiRef, onColumnOrderChange])

  const columnsWithFilters = useMemo(() => {
    return buildLeadTableColumnsWithFilters({
      columns,
      timezone,
      filterModel,
      onFilterModelChange: handleFilterModelChange,
    })
  }, [columns, timezone, filterModel, handleFilterModelChange])

  const slotProps = useMemo(
    () => ({
      footer: {
        permitAiAnalysis,
        totalCount: rowCount,
        loading,
        requestBodyJson,
      },
      columnsManagement: {
        columns,
        columnVisibilityModel,
        onColumnVisibilityModelChange,
        onResetColumns,
      },
      filterPanel: {
        sx: {
          px: 3,
          pt: '3px',
          pb: 2,
          minWidth: 640,
          '.MuiDataGrid-filterForm': {
            gap: 2,
            alignItems: 'center',
            justifyContent: 'flex-end',
          },
          '.MuiDataGrid-filterFormLogicOperatorInput': {
            width: 75,
          },
          '.MuiDataGrid-filterFormColumnInput, .MuiDataGrid-filterFormOperatorInput, .MuiDataGrid-filterFormValueInput': {
            width: 160,
          },
          '.MuiDataGrid-filterFormDeleteIcon': {
            mr: 2,
          },
          '.MuiDataGrid-filterFormLogicOperatorInput + .MuiDataGrid-filterFormColumnInput': {
            ml: 0,
          },
          '.MuiDataGrid-panelFooter': {
            mt: 1,
            pt: 3,
            px: 0,
            mx: 1,
            borderTop: `1px solid ${COLORS.lightGray}`,
            justifyContent: 'space-between',
            flexDirection: 'row-reverse',
          },
          '.MuiDataGrid-panelFooter .MuiButton-contained': {
            backgroundImage: 'none',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            border: '1px solid',
            height: '40px',
            fontSize: '14px',
          },
          '.MuiDataGrid-panelFooter .MuiButton-contained:hover': {
            backgroundImage: 'none',
            boxShadow: 'none',
          },
          '.MuiDataGrid-panelFooter button:first-of-type': {
            color: COLORS.frenchBlue,
            borderColor: COLORS.beauBlue,
          },
          '.MuiDataGrid-panelFooter button:last-of-type': {
            color: COLORS.darkCopperRed,
            borderColor: COLORS.paleCopper,
          },
          '.MuiDataGrid-panelFooter button:last-of-type .MuiButton-startIcon svg': {
            display: 'none',
          },
          '.MuiDataGrid-panelFooter button:last-of-type .MuiButton-startIcon::before': {
            content: '"×"',
            fontSize: '20px',
            lineHeight: 1,
          },
        },
      },
      toolbar: {
        onResetFilters,
        onExport,
        onImport,
        refreshTable,
        exportStatus,
        columns,
        filterModel,
        onFilterModelChange: handleFilterModelChange,
        permitUpload,
        loading,
        permitAiAnalysis,
        clientIdToName,
        clientCampaignIdToName,
        vendorIdToName,
        degreeProgramIdToName,
        statuses,
        dispositions,
        tableSettings,
        updateTableSettings,
      },
    }),
    [permitAiAnalysis, rowCount, loading, requestBodyJson, columns, columnVisibilityModel, onColumnVisibilityModelChange, onResetColumns, onResetFilters, onExport, onImport, refreshTable, exportStatus, filterModel, handleFilterModelChange, permitUpload, clientIdToName, clientCampaignIdToName, vendorIdToName, degreeProgramIdToName, statuses, dispositions, tableSettings, updateTableSettings]
  )

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columnsWithFilters}
        error={error ? 'Failed to fetch Leads. Please try again.' : null}
        apiRef={apiRef}
        onColumnOrderChange={handleColumnOrderChange}
        rowCount={rowCount}
        loading={loading}
        paginationMode='server'
        sortingMode='server'
        filterMode='server'
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        filterModel={muiFilterModel}
        onFilterModelChange={handleMuiFilterModelChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        columnHeaderHeight={56}
        disableRowSelectionOnClick
        disableRowGrouping
        disableAggregation
        disablePivoting
        filterDebounceMs={400}
        showToolbar
        slots={DATA_GRID_SLOTS}
        slotProps={slotProps}
        initialState={DATA_GRID_INITIAL_STATE}
        getRowClassName={getLeadRowClassName}
        sx={DATA_GRID_SX}
      />

      <LeadDetailsDrawer columns={columns} lead={showLeadDetails} onClose={handleHideDetails} />
    </div>
  )
}
